import { Search } from '@/components/ui/icons';
import { useEffect, useState, type RefObject } from 'react';
import { Input } from '@/components/ui/Input';
import { Popover } from '@/components/ui/Popover';
import { Spinner } from '@/components/ui/Spinner';
import { findGifs, type GifResult } from '@/lib/gifs';

/** Trending GIFs, or search results as you type; picking one sends it. */
export function GifPicker({ open, onClose, anchor, onSelect }: { open: boolean; onClose: () => void; anchor: RefObject<HTMLElement | null>; onSelect: (gif: GifResult) => void }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<GifResult[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    const timer = setTimeout(
      () => {
        setError(false);
        findGifs(query, controller.signal)
          .then(setResults)
          .catch(() => !controller.signal.aborted && setError(true));
      },
      query ? 350 : 0,
    );
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [open, query]);

  return (
    <Popover open={open} onClose={onClose} anchor={anchor} placement="top-end" label="GIFs" className="w-[min(420px,calc(100vw-16px))] p-3">
      <Input aria-label="Search GIFs" placeholder="Search GIFs" leading={<Search size={16} />} value={query} onChange={(e) => setQuery(e.target.value)} autoFocus />
      <div className="mt-2 h-[340px] overflow-y-auto pr-1">
        {error ? (
          <p className="py-10 text-center text-sm text-fg-muted">Couldn&apos;t load GIFs right now. Try again in a moment.</p>
        ) : !results ? (
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        ) : results.length === 0 ? (
          <p className="py-10 text-center text-sm text-fg-muted">No GIFs found for “{query}”.</p>
        ) : (
          <div className="columns-2 gap-2">
            {results.map((gif) => (
              <button
                key={gif.id}
                type="button"
                title={gif.title}
                aria-label={`Send GIF: ${gif.title}`}
                onClick={() => onSelect(gif)}
                className="mb-2 block w-full overflow-hidden rounded-lg bg-sunken outline-none focus-visible:ring-2 focus-visible:ring-accent"
              >
                <img src={gif.previewUrl} alt="" loading="lazy" referrerPolicy="no-referrer" width={gif.width} height={gif.height} className="block h-auto w-full" />
              </button>
            ))}
          </div>
        )}
      </div>
      <p className="mt-2 text-right text-[11px] font-bold tracking-wide text-fg-muted uppercase">Powered by KLIPY</p>
    </Popover>
  );
}
